/**
 * Error Boundary
 * 
 * Ce composant capture les erreurs React dans ses composants enfants
 * et affiche une UI de fallback au lieu de crasher toute l'application.
 * 
 * Avantages:
 * - Empêche l'application de crasher complètement
 * - Permet de logger les erreurs pour le debugging
 * - Offre une UI de récupération à l'utilisateur
 */

import React from "react";

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Met à jour l'état pour afficher l'UI de fallback au prochain rendu
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Logger l'erreur pour le debugging
    console.error("Error Boundary caught an error:", error, errorInfo);
    
    // Ici, vous pourriez envoyer l'erreur à un service de logging (Sentry, LogRocket, etc.)
    // logErrorToService(error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: "20px",
          textAlign: "center",
          background: "var(--bg-soft, #f5f5f5)",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}>
          <div style={{
            maxWidth: "500px",
            background: "var(--card-soft, #fff)",
            padding: "24px",
            borderRadius: "12px",
            boxShadow: "var(--shadow-md, 0 2px 8px rgba(0,0,0,0.1))",
          }}>
            <h2 style={{ color: "var(--brand-xd, #352394)", marginBottom: "16px" }}>
              Une erreur est survenue
            </h2>
            <p style={{ color: "var(--muted, #666)", marginBottom: "20px" }}>
              L'application a rencontré une erreur inattendue. Vous pouvez réessayer
              ou rafraîchir la page.
            </p>
            
            {process.env.NODE_ENV === "development" && this.state.error && (
              <details style={{
                textAlign: "left",
                background: "var(--bg, #f0f0f0)",
                padding: "12px",
                borderRadius: "8px",
                marginBottom: "20px",
                fontSize: "12px",
                overflow: "auto",
              }}>
                <summary style={{ cursor: "pointer", fontWeight: "bold", marginBottom: "8px" }}>
                  Détails de l'erreur (dev only)
                </summary>
                <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
            
            <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
              <button
                onClick={this.handleReset}
                style={{
                  padding: "10px 20px",
                  background: "var(--brand-xd, #352394)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontWeight: "600",
                }}
              >
                Réessayer
              </button>
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: "10px 20px",
                  background: "var(--line-strong, #ddd)",
                  color: "#333",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontWeight: "600",
                }}
              >
                Rafraîchir la page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
